/**
 * Contact sales, the paid-key intake for the air surfaces. aircargomcp / airratesmcp are metered paid
 * API products, so a prospect needs a way to ask for a quote and a production key. This posts to the
 * shared navo24 family intake (api.trackingmcp.com/v1/contact-sales), the same endpoint the sibling
 * products use, tagged with topic 'api' and this surface as the source. On any failure we fall back to
 * a mailto:sales@navo24.com link so a lead is never lost.
 *
 * Styling rides the navo24 token contract (card / btn-accent / mono / text-ink…), same as RateForm and
 * AircargoHome, so it inherits the light cream ground and flips theme for free.
 */

import { type FormEvent, useState } from 'react'
import { Layout } from '../components/Layout'
import { PageMeta } from '../lib/meta'
import { SURFACE } from '../surface'

// The shared family intake. Deployed and owned by the navo24 platform (NOT the per-surface air API),
// so this host is intentionally hard-coded rather than read from VITE_API_BASE.
const CONTACT_ENDPOINT = 'https://api.trackingmcp.com/v1/contact-sales'
const SALES_EMAIL = 'sales@navo24.com'

// Which product this lead came from, per the intake contract ('aircargo' | 'airrates').
const SOURCE = SURFACE === 'airrates' ? 'airrates' : 'aircargo'

type State =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'done'; email: string }
  | { kind: 'error' }

export function ContactPage() {
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [state, setState] = useState<State>({ kind: 'idle' })

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const ready = emailValid && state.kind !== 'submitting'

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!emailValid) return
    setState({ kind: 'submitting' })
    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          company: company.trim(),
          message: message.trim(),
          topic: 'api',
          source: SOURCE,
        }),
      })
      const json = (await res.json().catch(() => null)) as { ok?: boolean } | null
      if (res.ok && json?.ok) setState({ kind: 'done', email: email.trim() })
      else setState({ kind: 'error' })
    } catch {
      setState({ kind: 'error' })
    }
  }

  return (
    <Layout>
      <PageMeta
        title="Contact sales for a quote | AirCargoMCP"
        description="Talk to us about a production API key. Tell us your monthly volume and use-case, and the team will price a plan for your air-cargo tracking and rate needs."
        path="/contact"
      />
      <section className="mx-auto max-w-2xl px-5 pt-14 pb-8">
        <span className="eyebrow">Talk to sales</span>
        <h1 className="mt-4 text-[clamp(2rem,4.6vw,2.9rem)] font-light tracking-[-0.03em] text-ink">
          Contact sales for a <span className="hl">quote</span>.
        </h1>
        <p className="mt-4 max-w-xl text-[16px] leading-[1.55] text-muted">
          The API and MCP surface is a metered, paid product. Tell us your expected monthly volume
          and what you are building, and the team will price a plan and issue a production key.
        </p>

        {state.kind === 'done' ? (
          <div className="reveal card mt-8 border-l-2 border-l-signal p-6">
            <div className="text-lg font-semibold text-ink">Thanks, we have your request.</div>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              The team will reply to <span className="mono text-ink">{state.email}</span> shortly,
              with pricing and next steps.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="card mt-8 p-6 sm:p-7">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="min-w-0">
                <label
                  htmlFor="cs-name"
                  className="mono text-[11px] uppercase tracking-wider text-faint"
                >
                  Name
                </label>
                <input
                  id="cs-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  disabled={state.kind === 'submitting'}
                  className="mt-1.5 w-full rounded-xl border border-border bg-surface-0 px-3.5 py-2.5 text-[15px] text-ink outline-none transition-colors placeholder:text-faint focus:border-signal disabled:opacity-60"
                  placeholder="Jane Rivera"
                />
              </div>
              <div className="min-w-0">
                <label
                  htmlFor="cs-company"
                  className="mono text-[11px] uppercase tracking-wider text-faint"
                >
                  Company (optional)
                </label>
                <input
                  id="cs-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  autoComplete="organization"
                  disabled={state.kind === 'submitting'}
                  className="mt-1.5 w-full rounded-xl border border-border bg-surface-0 px-3.5 py-2.5 text-[15px] text-ink outline-none transition-colors placeholder:text-faint focus:border-signal disabled:opacity-60"
                  placeholder="Acme Forwarding"
                />
              </div>
            </div>

            <div className="mt-5 min-w-0">
              <label
                htmlFor="cs-email"
                className="mono text-[11px] uppercase tracking-wider text-faint"
              >
                Work email <span className="text-down">required</span>
              </label>
              <input
                id="cs-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={state.kind === 'submitting'}
                className="mt-1.5 w-full rounded-xl border border-border bg-surface-0 px-3.5 py-2.5 text-[15px] text-ink outline-none transition-colors placeholder:text-faint focus:border-signal disabled:opacity-60"
                placeholder="jane@acmeforwarding.com"
              />
            </div>

            <div className="mt-5 min-w-0">
              <label
                htmlFor="cs-message"
                className="mono text-[11px] uppercase tracking-wider text-faint"
              >
                What do you need?
              </label>
              <textarea
                id="cs-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                disabled={state.kind === 'submitting'}
                className="mt-1.5 w-full resize-y rounded-xl border border-border bg-surface-0 px-3.5 py-2.5 text-[15px] leading-relaxed text-ink outline-none transition-colors placeholder:text-faint focus:border-signal disabled:opacity-60"
                placeholder="Roughly how many AWB lookups or rate quotes per month, and what you are building (dashboard, agent, TMS integration)."
              />
            </div>

            <button
              type="submit"
              disabled={!ready}
              className="btn-accent mt-6 w-full rounded-xl px-6 py-3 text-center font-semibold disabled:cursor-not-allowed disabled:opacity-45"
            >
              {state.kind === 'submitting' ? 'Sending…' : 'Request a quote'}
            </button>

            {state.kind === 'error' && (
              <p className="reveal mt-4 text-sm leading-relaxed text-muted">
                Something went wrong sending that. Email us directly at{' '}
                <a
                  className="font-semibold text-signal hover:underline"
                  href={`mailto:${SALES_EMAIL}`}
                >
                  {SALES_EMAIL}
                </a>{' '}
                and we will pick it up.
              </p>
            )}

            <p className="mt-4 text-xs leading-relaxed text-faint">
              Prefer email? Reach the team any time at{' '}
              <a className="text-muted hover:text-ink" href={`mailto:${SALES_EMAIL}`}>
                {SALES_EMAIL}
              </a>
              .
            </p>
          </form>
        )}
      </section>
    </Layout>
  )
}
